import type { MutableRefObject } from 'react'
import type { SocketProps } from 'src/utils/socket'
import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { autorun } from 'mobx'
import ResizeObserver from 'resize-observer-polyfill'
import { axiosStore } from 'src/stores'
import { $evt } from 'src/plugins'
import { isEqual, debounce } from 'lodash'
import queryString from 'query-string'
import FakeProgress from 'src/utils/fakeProgress'
import Socket from 'src/utils/socket'

export function useLatest<T> (value: T) {
  const ref = useRef(value)
  ref.current = value

  return ref
}

export function usePrevious (value: any) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export function useWindowSize () {
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0
  })

  useEffect(() => {
    function handleResize () {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }
    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

export function useDomSize (
  ref: MutableRefObject<HTMLElement | null>,
  selector?: string
) {
  const [domSize, setDomSize] = useState({ clientWidth: 0, clientHeight: 0 })

  useEffect(() => {
    const getDomSize = (dom: any) => {
      const { clientWidth, clientHeight } = dom
      const now = { clientWidth, clientHeight }
      setDomSize(prev => isEqual(prev, now) ? prev : now)
    }

    if (!ref.current) return

    let timer: any = -1
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        clearTimeout(timer)
        timer = setTimeout(() => {
          getDomSize(entry.target)
        }, 100)
      }
    })

    ro.observe(selector ? ref.current.querySelector(selector)! : ref.current)

    getDomSize(ref.current)

    return () => {
      clearTimeout(timer)
      ro.disconnect()
    }
  }, [])

  return domSize
}

export function useIsFullScreen () {
  const [isFullScreen, setIsFullScreen] = useState(false)

  const winSize = useWindowSize()
  useEffect(() => {
    const fullscreen = winSize.width === window.screen.width && winSize.height === window.screen.height
    fullscreen !== isFullScreen && setIsFullScreen(fullscreen)
  }, [winSize, isFullScreen])

  return isFullScreen
}

export function useDomIsMatchFullScreenDom (domClassName: string) {
  const [isMatch, setIsMatch] = useState(false)

  const isFullScreen = useIsFullScreen()
  useEffect(() => {
    setIsMatch(isFullScreen ? document.fullscreenElement === document.getElementsByClassName(domClassName)[0] : false)
  }, [isFullScreen])

  return isMatch
}

export function useIsLoading (paths: string[], allPath: boolean = false) {
  const [loading, setLoading] = useState(false)
  useEffect(() => autorun(
    () => {
      if (allPath) {
        setLoading(axiosStore.pending.all)
        return
      }
      let loading = false
      for (let i = 0, j = paths.length; i < j; i++) {
        if (paths[i] && axiosStore.pending[paths[i]]) {
          loading = true
          break
        }
      }
      setLoading(loading)
    }
  ), [paths, allPath])

  return loading
}

type VisibleView = {
  id: string,
  visible: boolean
}
export function useScrollToWhichView (anchors: string[], scrollDomId?: string, throttle: number = 20) {
  const [views, setViews] = useState<VisibleView[]>([])

  useEffect(() => {
    const scrollDom = scrollDomId ? document.getElementById(scrollDomId) || window : window
    const handleWinScroll = debounce(() => {
      const pos: VisibleView[] = []
      anchors.forEach((id: string) => {
        const dom = document.getElementById(id)
        const rect = dom?.getBoundingClientRect()
        pos.push({
          id,
          visible: !!rect && rect.top >= 0 && rect.bottom <= window.innerHeight
        })
      })
      setViews(pos)
    }, throttle)
    scrollDom.addEventListener('scroll', handleWinScroll)

    return () => scrollDom.removeEventListener('scroll', handleWinScroll)
  }, [])

  return views
}

/**
 * DOM 节点拖动
 * @param draggable 是否可拖动
 * @param wrapperClass 需要拖动的节点的 className
 * @param dragClass 拖动区域节点的 className，如果为 boolean 值则表示同 wrapperClass
 * @param onEvts 对外暴露的拖动事件
 */
export function useDomDraggable (
  draggable: boolean,
  wrapperClass: string,
  dragClass: string | boolean,
  onEvts?: {
    onInit?: (wrapperDom: HTMLElement, dragDom: HTMLElement) => void,
    onDragStart?: (wrapperDom: HTMLElement, dragDom: HTMLElement, position: any, mouseEvt: any) => void,
    onDrag?: (wrapperDom: HTMLElement, dragDom: HTMLElement, position: any, mouseEvt: any) => void,
    onDragStop?: (wrapperDom: HTMLElement, dragDom: HTMLElement, position: any, mouseEvt: any) => void,
  }
) {
  const isDragStart = useRef(false)
  const prevPosition = useRef({ offsetLeft: 0, offsetTop: 0, pageX: 0, pageY: 0 })
  useEffect(() => {
    let wrapperDom: any, dragDom: any

    // 鼠标按下，可开始拖动
    const onMousedown = (params: any) => {
      // 记录 wrapperDom 左上顶点距离视窗左上位置的距离
      prevPosition.current.offsetLeft = wrapperDom.offsetLeft
      prevPosition.current.offsetTop = wrapperDom.offsetTop
      // 计算鼠标位置距离 dragDom 左上顶点的位置
      prevPosition.current.pageX = params.pageX - wrapperDom.offsetLeft
      prevPosition.current.pageY = params.pageY - wrapperDom.offsetTop
      isDragStart.current = true
      onEvts?.onDragStart?.(wrapperDom, dragDom, prevPosition.current, params)
    }

    // 鼠标释放，停止拖动
    const onMouseup = (params: any) => {
      isDragStart.current = false
      onEvts?.onDragStop?.(wrapperDom, dragDom, prevPosition.current, params)
    }

    // 鼠标移动时进行拖动
    const onMousemove = (params: any) => {
      if (isDragStart.current) {
        prevPosition.current.offsetLeft = params.pageX - prevPosition.current.pageX
        prevPosition.current.offsetTop = params.pageY - prevPosition.current.pageY
        onEvts?.onDrag?.(wrapperDom, dragDom, prevPosition.current, params)
      }
    }

    if (draggable) {
      // 定时是为了保证 wrapperDom 和 dragDom 都生成完毕
      setTimeout(() => {
        wrapperDom = document.getElementsByClassName(wrapperClass)[0]
        dragDom = typeof dragClass === 'boolean' ? wrapperDom : wrapperDom?.querySelector(`.${dragClass}`)
        if (wrapperDom && dragDom) {
          // 添加手型鼠标指示器
          onEvts?.onInit?.(wrapperDom, dragDom)
          // 鼠标按下，可开始拖动
          dragDom.addEventListener('mousedown', onMousedown)
          dragDom.addEventListener('mouseup', onMouseup)
          // 在 document 上监听鼠标移动的事件
          document.addEventListener('mousemove', onMousemove)
        }
      }, 0)
    }

    return () => {
      dragDom?.removeEventListener('mousedown', onMousedown)
      dragDom?.removeEventListener('mouseup', onMouseup)
      document.removeEventListener('mousemove', onMousemove)
    }
  }, [draggable])
}

/**
 * 获取浏览器查询参数
 * @returns 
 */
export function useLocalSearch () {
  const location = useLocation()
  const localSearch = useMemo(() => {
    return queryString.parse(location.search)
  }, [location.search])
  return localSearch
}

/**
 * 检查浏览器是否从后台唤醒到前台，比如Tab切换
 * @param onChange 
 */
export function useBrowserVisibilityChange (
  onChange: (state: DocumentVisibilityState) => void
) {
  useEffect(() => {
    const handleChange = () => {
      onChange(document.visibilityState)
    }

    document.addEventListener('visibilitychange', handleChange)

    return () => {
      document.removeEventListener('visibilitychange', handleChange)
    }
  }, [])
}

/**
 * 检查浏览器Tab focus
 * @param onFocus 
 */
export function useBrowserTabFocus (
  onFocus: () => void,
  onBlur?: () => void
) {
  useEffect(() => {
    const handleBlur = () => {
      onBlur?.()
    }

    window.addEventListener('focus', onFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])
}

/**
 * $evt 的简单集成
 * @param evt 
 * @param handle 
 */
export function useEvt (
  evt: string,
  handle: (...args: any[]) => void,
  deps?: ReadonlyArray<unknown>
) {
  useEffect(() => {
    $evt.addListener(evt, handle)

    return () => {
      $evt.removeListener(evt, handle)
    }
  }, deps ?? [])
}

/**
 * Socket 的简单集成
 * @param params 
 */
export function useSocket (
  params: SocketProps,
  deps?: ReadonlyArray<unknown>
): MutableRefObject<Socket | undefined> {
  const socket = useRef<Socket>()
  useEffect(() => {
    socket.current = new Socket(params)

    return () => {
      socket.current?.close()
    }
  }, deps ?? [])

  return socket
}

// 假的进度条
type FakeOptions = {
  timeConstant?: number,
  updateInterval?: number,
}
export function useFakeProgress (
  start: boolean,
  options: FakeOptions = {
    timeConstant: 30000,
    updateInterval: 1000,
  }
) {
  const FP = useRef(new FakeProgress({ timeConstant: options.timeConstant }))

  const [progress, setProgress] = useState(0)

  const timer = useRef<NodeJS.Timeout>()
  useEffect(() => {
    start && FP.current.start()

    setProgress(FP.current.progress * 100)

    timer.current = setInterval(() => {
      setProgress(FP.current.progress * 100)
    }, options.updateInterval)

    return () => {
      FP.current.stop()
      clearInterval(timer.current)
    }
  }, [start])

  return progress
}

/**
 * 判断标签内容是否 ellipsis 溢出
 * @param ref 
 * @param selector 
 * @returns 
 */
export function useIsEllipsis (
  ref: MutableRefObject<HTMLElement | null>,
  selector?: string
) {
  const [isEllipsis, setIsEllipsis] = useState(false)

  useLayoutEffect(() => {
    const dom = selector ? ref.current?.querySelector(selector) as HTMLElement : ref.current
    const { clientWidth = 0, clientHeight = 0, scrollWidth = 0, scrollHeight = 0 } = dom ?? {}
    setIsEllipsis((clientWidth < scrollWidth) || (clientHeight < scrollHeight))
  })

  return isEllipsis
}

/**
 * 获取设备的像素密度 PixelRatio
 * @returns 
 */
export function useDevicePixelRatio () {
  const [pixelRadio, setPixelRadio] = useState(window.devicePixelRatio)

  useEffect(() => {
    const updatePixelRatio = () => {
      setPixelRadio(window.devicePixelRatio)
    }

    const mediaQueryList = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
    mediaQueryList.addEventListener('change', updatePixelRatio)

    return () => {
      mediaQueryList.removeEventListener('change', updatePixelRatio)
    }
  }, [])

  return pixelRadio
}

/**
 * 定时请求
 * @param func 
 * @param time 
 */
export function useInterval (func: Function, time: number = 3000) {
  useEffect(() => {
    func()

    let timer = setInterval(() => {
      func()
    }, time)

    return () => {
      clearInterval(timer)
    }
  }, [])
}